import {
  Coffee, Wind, Waves, Flame, Wifi, WashingMachine, Clock, Sunrise, Sparkles,
} from "lucide-react";
import { photoUrl } from "@/lib/api";
import selection from "@/data/photoSelection.json";
import { Reveal } from "@/components/Reveal";

const FEATURES = [
  { n: "01", icon: Coffee, title: "Designer Kitchen",
    body: "Marble bench, timber cabinetry and a barista-grade espresso machine — fully equipped for long, slow breakfasts." },
  { n: "02", icon: Wind, title: "Air Conditioning",
    body: "Fully ducted air conditioning throughout — stay cool and comfortable no matter what the tropical weather brings." },
  { n: "03", icon: Waves, title: "Swimming Pool",
    body: "A private pool framed by garden and deck — your own private piece of paradise." },
  { n: "04", icon: Flame, title: "Outdoor Deck & BBQ",
    body: "A long timber deck for slow dinners and island-style barbecues under the stars." },
  { n: "05", icon: Wifi, title: "Fast Wi-Fi",
    body: "Reliable, fast Wi-Fi throughout — work from paradise, if you must." },
  { n: "06", icon: WashingMachine, title: "Laundry",
    body: "Washing machine and laundry essentials, so you travel light and stay easy." },
];

export const House = ({ hideHeader = false }) => (
  <section id="house" className="bg-[hsl(var(--marble))]/65 py-28 md:py-36 relative">
    <div className="container-editorial">
      {!hideHeader && (
        <Reveal className="max-w-2xl">
          <div className="eyebrow-rule">The House</div>
          <h2 className="editorial-heading text-3xl md:text-4xl lg:text-[52px]">
            Considered comfort,<br />
            <em>stylishly Pacific.</em>
          </h2>
          <p className="mt-7 text-[hsl(var(--muted-foreground))] max-w-xl text-[15px] leading-relaxed">
            Designed for unhurried days — generous spaces, soft natural light, and
            everything you need to feel at home.
          </p>
        </Reveal>
      )}

      <div className={`${hideHeader ? "" : "mt-16 md:mt-24"} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--border))]/60 border border-[hsl(var(--border))]/60 rounded-2xl overflow-hidden`}>
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 80}>
            <div className="group relative h-full bg-[hsl(var(--card))] p-10 md:p-12 transition-colors duration-700 hover:bg-[hsl(var(--linen))]">
              <div className="flex items-start justify-between mb-8">
                <div className="font-serif-italic text-2xl text-[hsl(var(--oak-deep))]/80">{f.n}</div>
                <f.icon size={20} strokeWidth={1} className="text-[hsl(var(--ink))]/80" />
              </div>
              <h3 className="font-display text-[22px] tracking-wide mb-4">{f.title}</h3>
              <p className="text-[13.5px] text-[hsl(var(--muted-foreground))] leading-[1.85] max-w-xs">
                {f.body}
              </p>
              <span className="absolute left-10 right-10 bottom-6 h-px bg-[hsl(var(--oak-deep))]/30 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-700" />
            </div>
          </Reveal>
        ))}
      </div>

      {/* Stay details strip */}
      <Reveal>
        <div className="mt-16 rounded-2xl bg-[hsl(var(--ink))] text-[hsl(var(--linen))] p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[hsl(var(--sage))]/10 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {[
              { icon: Clock, eyebrow: "Check-in", v: "3:00 PM", tint: "sage" },
              { icon: Sunrise, eyebrow: "Check-out", v: "10:00 AM", tint: "oak" },
              { icon: Sparkles, eyebrow: "Flexible", v: "Early check-in & late departure by arrangement", tint: "neutral" },
            ].map((row) => (
              <div key={row.eyebrow} className="flex items-start gap-5">
                <row.icon
                  size={22}
                  strokeWidth={1.1}
                  className={
                    row.tint === "sage"
                      ? "mt-1 text-[hsl(var(--sage))]"
                      : row.tint === "oak"
                      ? "mt-1 text-[hsl(var(--oak))]"
                      : "mt-1 text-white/60"
                  }
                />
                <div>
                  <div className="tracking-eyebrow text-[10px] text-white/55 mb-2">{row.eyebrow}</div>
                  <div className="font-display text-lg leading-snug">{row.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Editorial info block */}
      <div className="mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5">
          <div className="eyebrow-rule">A note from the kitchen</div>
          <p className="font-serif-italic text-2xl md:text-3xl leading-snug text-[hsl(var(--ink))]">
            &ldquo;We designed the kitchen the way we&apos;d want our own — marble bench
            wide enough for a slow breakfast, timber that warms the room, and an
            espresso machine we&apos;re a little obsessed with.&rdquo;
          </p>
          <div className="mt-8 flex items-center gap-4">
            <span className="block h-px w-10 bg-[hsl(var(--oak-deep))]/60" />
            <p className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))]">
              AJ &amp; Brad
            </p>
          </div>
        </Reveal>

        <Reveal delay={140} className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-4 md:gap-5">
            <div className="img-frame aspect-[3/4]">
              <img
                src={photoUrl(selection.kitchen)}
                alt="Timber kitchen with marble benchtop"
                className="w-full h-full object-cover transition-transform duration-[1600ms] hover:scale-[1.03]"
                loading="lazy"
              />
            </div>
            <div className="img-frame aspect-[3/4] mt-10">
              <img
                src={photoUrl(selection.lounge)}
                alt="Curved cream lounge in living area"
                className="w-full h-full object-cover transition-transform duration-[1600ms] hover:scale-[1.03]"
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
